import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import InfinityList from '@/components/ui/custom/InfinityList';
import { Alert, Loading } from '@/components/ui/daisy';
import useMasterEvaluations from '@/data/hook/evaluation/useMasterEvaluations';
import Evaluation from '@/models/Evaluation';
import EvaluationBlockCard from './EvaluationBlockCard';
import EvaluationBlockFormList from './EvaluationBlockFormList';
import { EvaluationBlockInlineFormFields } from './EvaluationBlockInlineForm';
import { AssessmentEditState } from './EvaluationsTabContent';

type EvaluationBlockListProps = {
  courseLineId: number;
  onEditingAssessment: (state: AssessmentEditState) => void;
};

export default function EvaluationBlockList({
  courseLineId,
  onEditingAssessment,
}: EvaluationBlockListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });
  const {
    evaluations,
    slavesByMaster,
    isLoading,
    createEvaluation,
    isCreating,
    deleteEvaluation,
    updateEvaluation,
    isUpdating,
    cacheManager,
    ...infiniteProps
  } = useMasterEvaluations({ courseLineId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  const handleCreateBlock = async (data: EvaluationBlockInlineFormFields) => {
    await createEvaluation({
      name: data.name,
      coursePathIds: data.coursePaths.map(field => field.id),
    });
  };

  const handleUpdateBlock = async (
    evaluation: Evaluation,
    data: EvaluationBlockInlineFormFields,
  ) => {
    await updateEvaluation({
      evaluationId: evaluation.id,
      data: { name: data.name },
      coursePathIds: data.coursePaths.map(field => field.id),
    });
  };

  const getAppliedCoursesPaths = (evaluation: Evaluation) => {
    return (
      slavesByMaster.get(evaluation.id)?.map(entry => entry.coursePath) ?? []
    );
  };

  return (
    <>
      <ConditionalRenderer condition={!evaluations.length}>
        <Alert variant="soft">
          <InformationCircleIcon className="w-6 h-6 shrink-0 text-error" />
          <div className="flex flex-col">
            <span className="font-bold text-sm">{t('infoAlertTitle')}</span>
            <span className="text-xs">{t('infoAlertDescription')}</span>
          </div>
        </Alert>
      </ConditionalRenderer>
      <ConditionalRenderer condition={evaluations.length > 0}>
        <InfinityList
          {...infiniteProps}
          className="relative flex flex-col gap-6 h-fit"
        >
          {evaluations.map(evaluation => (
            <EvaluationBlockCard
              key={evaluation.id}
              evaluation={evaluation}
              cacheManager={cacheManager}
              appliedCoursePaths={getAppliedCoursesPaths(evaluation)}
              courseLineId={courseLineId}
              onDelete={deleteEvaluation}
              onUpdate={handleUpdateBlock}
              isUpdating={isUpdating}
              onAddAssessment={() => onEditingAssessment({ evaluation })}
              onEditAssessment={assessment =>
                onEditingAssessment({ evaluation, assessment })
              }
            />
          ))}
        </InfinityList>
      </ConditionalRenderer>
      <EvaluationBlockFormList
        onSubmit={handleCreateBlock}
        isLoading={isCreating}
        courseLineId={courseLineId}
      />
    </>
  );
}
